import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    DollarSign, Calculator, ChevronDown, ChevronUp,
    TrendingUp, TrendingDown, Minus, Equal, RefreshCw, FileText, Receipt, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import ARChargesTable from './ARChargesTable';
import APChargesTable from './APChargesTable';
import { useLanguage } from '../../../context/LanguageContext';
import { baseURL } from '@/config/APIConfig';


const API_URL = baseURL.AiBaseURL;

const ARAPChargesContainer = ({
    bookingId,
    bookingNo,
    arCharges,
    setArCharges,
    apCharges,
    setApCharges,
    currencyList = [],
    basisList = [],
    containerList = [],
    chargeCodeList = [],
    customerId,
    customerName,
}) => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [viewMode, setViewMode] = useState('both'); // 'ar', 'ap', 'both'
    const [isExpanded, setIsExpanded] = useState(true);
    const [isSyncingAR, setIsSyncingAR] = useState(false);
    const [isSyncingAP, setIsSyncingAP] = useState(false);
    const [accountingStatus, setAccountingStatus] = useState(null);

    // Calculate totals
    const totalAR = arCharges.reduce((sum, c) => {
        const amount = parseFloat(c.amount) || 0;
        const qty = parseFloat(c.quantity) || 1;
        return sum + (amount * qty);
    }, 0);

    const totalAP = apCharges.reduce((sum, c) => {
        const amount = parseFloat(c.amount) || 0;
        const qty = parseFloat(c.quantity) || 1;
        return sum + (amount * qty);
    }, 0);

    const profit = totalAR - totalAP;
    const margin = totalAR > 0 ? ((profit / totalAR) * 100) : 0;

    // Fetch accounting status for this booking
    const fetchAccountingStatus = useCallback(async () => {
        if (!bookingId || bookingId === 0) return;

        try {
            const response = await fetch(`${API_URL}/api/accounting/sync/booking/${bookingId}`);
            if (response.ok) {
                const data = await response.json();
                setAccountingStatus(data);
                // console.log('Fetched accounting status:', data);
                // Update AR charges with invoice status
                if (data.ar_charge_map && Object.keys(data.ar_charge_map).length > 0) {
                    setArCharges(prev => prev.map(charge => {
                        const invoiceInfo = data.ar_charge_map[charge.id];
                        if (invoiceInfo) {
                            return {
                                ...charge,
                                invoiceId: invoiceInfo.invoice_id,
                                invoiceNo: invoiceInfo.invoice_no,
                                invoiceStatus: invoiceInfo.invoice_status === 'paid' ? 'invoiced' :
                                    invoiceInfo.invoice_status === 'issued' ? 'invoiced' : 'draft'
                            };
                        }
                        return charge;
                    }));
                }

                // Update AP charges with bill status
                if (data.ap_charge_map && Object.keys(data.ap_charge_map).length > 0) {
                    setApCharges(prev => prev.map(charge => {
                        const billInfo = data.ap_charge_map[charge.id];
                        if (billInfo) {
                            return {
                                ...charge,
                                billId: billInfo.bill_id,
                                billNo: billInfo.bill_no,
                                billStatus: billInfo.bill_status === 'paid' ? 'billed' :
                                    billInfo.bill_status === 'approved' ? 'billed' : 'draft'
                            };
                        }
                        console.log('No bill info for charge ID:', charge);
                        return charge;
                    }));
                }
            }
        } catch (error) {
            console.error('Error fetching accounting status:', error);
        }
    }, [bookingId, setArCharges, setApCharges]);

    useEffect(() => {
        // console.log('Fetching accounting status for booking ID:', bookingId);
        fetchAccountingStatus();
    }, [fetchAccountingStatus]);

    const handleJobCosting = () => {
        if (bookingId && bookingId !== 0) {
            navigate(`/job-costing/${bookingId}`);
        } else {
            toast.error('Please save the booking first to access Job Costing.');
        }
    };

    // Sync AR charges to Customer Invoices
    const handleSyncARToInvoice = async () => {
        if (!bookingId || bookingId === 0) {
            toast.error('Please save the booking first before syncing to invoices.');
            return;
        }

        // Filter charges that have a Bill To Party
        const chargesWithParty = arCharges.filter(c => c.billToPartyId && c.billToPartyId !== 0 && c.invoiceId !== '' && c.invoiceId !== null);

        if (chargesWithParty.length === 0) {
            toast.warning('No AR charges with Bill To Party to sync. Please assign a Bill To Party to at least one charge.');
            return;
        }

        setIsSyncingAR(true);
        try {
            const response = await fetch(`${API_URL}/api/accounting/sync/ar-to-invoice`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    booking_id: String(bookingId),
                    booking_no: bookingNo,
                    charges: chargesWithParty,
                    customer_id: String(customerId),
                    customer_name: customerName
                })
            });

            const result = await response.json();

            if (response.ok) {
                toast.success(result.message);

                // Update charge statuses
                if (result.updated_charges) {
                    setArCharges(prev => prev.map(charge => {
                        const update = result.updated_charges.find(u => u.charge_id === charge.id);
                        if (update) {
                            return {
                                ...charge,
                                invoiceId: update.invoice_id,
                                invoiceNo: update.invoice_no,
                                invoiceStatus: update.invoice_status
                            };
                        }
                        return charge;
                    }));
                }

                // Refresh accounting status
                await fetchAccountingStatus();
            } else {
                toast.error(result.detail || 'Failed to sync AR charges');
            }
        } catch (error) {
            console.error('Error syncing AR to invoice:', error);
            toast.error('Failed to sync AR charges to invoices');
        } finally {
            setIsSyncingAR(false);
        }
    };

    // Sync AP charges to Vendor Bills
    const handleSyncAPToBill = async () => {
        if (!bookingId || bookingId === 0) {
            toast.error('Please save the booking first before syncing to bills.');
            return;
        }

        // Filter charges that have a Vendor
        const chargesWithVendor = apCharges.filter(c => c.vendorId && c.vendorId !== 0 && c.billId !== '' && c.billId !== null);

        if (chargesWithVendor.length === 0) {
            toast.warning('No AP charges with Vendor to sync. Please assign a Vendor to at least one charge.');
            return;
        }

        setIsSyncingAP(true);
        try {
            console.log('Syncing AP charges to bills with payload:', {
                booking_id: String(bookingId),
                booking_no: bookingNo,
                charges: chargesWithVendor
            });
            const response = await fetch(`${API_URL}/api/accounting/sync/ap-to-bill`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    booking_id: String(bookingId),
                    booking_no: bookingNo,
                    charges: chargesWithVendor
                })
            });

            const result = await response.json();

            if (response.ok) {
                toast.success(result.message);

                // Update charge statuses
                if (result.updated_charges) {
                    setApCharges(prev => prev.map(charge => {
                        const update = result.updated_charges.find(u => u.charge_id === charge.id);
                        if (update) {
                            return {
                                ...charge,
                                billId: update.bill_id,
                                billNo: update.bill_no,
                                billStatus: update.bill_status
                            };
                        }
                        return charge;
                    }));
                }

                // Refresh accounting status
                await fetchAccountingStatus();
            } else {
                toast.error(result.detail || 'Failed to sync AP charges');
            }
        } catch (error) {
            console.error('Error syncing AP to bill:', error);
            toast.error('Failed to sync AP charges to bills');
        } finally {
            setIsSyncingAP(false);
        }
    };

    return (
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-400 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
            {/* Header */}
            <div
                className="w-full flex items-center justify-between p-4 bg-gradient-to-r to-slate-100 dark:from-slate-800 dark:to-slate-700 cursor-pointer"
                onClick={() => setIsExpanded(!isExpanded)}
                data-testid="arap-charges-header"
            >
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg shadow-md bg-gradient-to-br from-[#2563EB] to-[#14B8A6]">
                        <Receipt className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                            {t('arApInformation')}
                        </h2>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            {t('manageRevenueAndCost')}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 ">
                    {/* View Toggle */}
                    <div className="flex items-center border border-gray-300 rounded-lg p-1" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={() => setViewMode('ar')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${viewMode === 'ar'
                                ? ' text-blue-600 dark:text-blue-400 shadow-sm bg-slate-200 dark:bg-slate-600'
                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-600'
                                }`}
                            data-testid="view-mode-ar"
                        >
                            AR
                        </button>
                        <button
                            onClick={() => setViewMode('ap')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${viewMode === 'ap'
                                ? ' text-blue-600 dark:text-blue-400 shadow-sm bg-slate-200 dark:bg-slate-600'
                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-600'
                                }`}
                            data-testid="view-mode-ap"
                        >
                            AP
                        </button>
                        <button
                            onClick={() => setViewMode('both')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${viewMode === 'both'
                                ? ' text-blue-600 dark:text-blue-400 shadow-sm bg-slate-200 dark:bg-slate-600'
                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-600'
                                }`}
                            data-testid="view-mode-both"
                        >
                            Both
                        </button>
                    </div>

                    {/* Job Costing Button */}
                    <Button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleJobCosting();
                        }}
                        className=" flex items-center border border-gray-300 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 text-xs"
                        size="lg"
                        data-testid="job-costing-btn"
                        variant="outline"
                    >
                        <Calculator className="w-4 h-4" />
                        {t('jobCosting')}
                    </Button>

                    {/* Expand/Collapse */}
                    <button className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded" data-testid="toggle-expand">
                        {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-slate-500" />
                        ) : (
                            <ChevronDown className="w-5 h-5 text-slate-500" />
                        )}
                    </button>
                </div>
            </div>

            {/* Content */}
            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="p-4 space-y-6 border-t border-slate-200 dark:border-slate-800"
                    >

                        {/* Accounting Status Summary */}
                        {accountingStatus && (accountingStatus?.invoices?.length > 0 || accountingStatus.bills?.length > 0) && (
                            <div className="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-200 dark:border-slate-700">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4 text-xs">
                                        {accountingStatus.invoices?.length > 0 && (
                                            <div className="flex items-center gap-2">
                                                <Receipt className="w-4 h-4 text-amber-500" />
                                                <span className="text-slate-600 dark:text-slate-400">
                                                    {accountingStatus.invoices.length} Invoice(s) -
                                                    <span className="font-semibold text-amber-600 ml-1">
                                                        ${accountingStatus.total_ar?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                                                    </span>
                                                    {accountingStatus.ar_collected > 0 && (
                                                        <span className="text-green-600 ml-1">
                                                            (${accountingStatus.ar_collected.toLocaleString('en-US', { minimumFractionDigits: 2 })} collected)
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                        {accountingStatus.bills?.length > 0 && (
                                            <div className="flex items-center gap-2">
                                                <FileText className="w-4 h-4 text-green-500" />
                                                <span className="text-slate-600 dark:text-slate-400">
                                                    {accountingStatus.bills.length} Bill(s) -
                                                    <span className="font-semibold text-green-600 ml-1">
                                                        ${accountingStatus.total_ap?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                                                    </span>
                                                    {accountingStatus.ap_paid > 0 && (
                                                        <span className="text-blue-600 ml-1">
                                                            (${accountingStatus.ap_paid.toLocaleString('en-US', { minimumFractionDigits: 2 })} paid)
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={fetchAccountingStatus}
                                        className="text-xs"
                                        data-testid="refresh-status-btn"
                                    >
                                        <RefreshCw className="w-3 h-3 mr-1" />
                                        {t('refresh')}
                                    </Button>
                                </div>
                            </div>
                        )}


                        {/* AR Table */}
                        {(viewMode === 'ar' || viewMode === 'both') && (
                            <ARChargesTable
                                charges={arCharges}
                                setCharges={setArCharges}
                                currencyList={currencyList}
                                basisList={basisList}
                                containerList={containerList}
                                chargeCodeList={chargeCodeList}
                                onSyncToInvoice={handleSyncARToInvoice}
                                isSyncing={isSyncingAR}
                                bookingId={bookingId}
                                totalinvoice={accountingStatus?.invoices?.length}
                            />
                        )}

                        {/* AP Table */}
                        {(viewMode === 'ap' || viewMode === 'both') && (
                            <APChargesTable
                                charges={apCharges}
                                setCharges={setApCharges}
                                currencyList={currencyList}
                                basisList={basisList}
                                containerList={containerList}
                                chargeCodeList={chargeCodeList}
                                onSyncToBill={handleSyncAPToBill}
                                isSyncing={isSyncingAP}
                                bookingId={bookingId}
                                totalbill={accountingStatus?.bills?.length}
                            />
                        )}

                        {/* Profit Summary - Only show in Both mode */}
                        {viewMode === 'both' && (
                          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
  {/* Total AR Card */}
  <div className="rounded-xl border border-gray-100 dark:border-slate-700 p-4">
    <div className="text-[11px] uppercase tracking-wide text-gray-500">
      {t('totalAR')} (USD)
    </div>
    <div className="mt-1 text-[20px] font-semibold text-green-600 tabular-nums">
      ${totalAR.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </div>
  </div>

  {/* Total AP Card */}
  <div className="rounded-xl border border-gray-100 dark:border-slate-700 p-4">
    <div className="text-[11px] uppercase tracking-wide text-gray-500">
      {t('totalAP')} (USD)
    </div>
    <div className="mt-1 text-[20px] font-semibold text-red-600 tabular-nums">
      ${totalAP.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </div>
  </div>

  {/* Profit / Margin Card */}
  <div className="rounded-xl border border-gray-100 dark:border-slate-700 p-4 bg-gradient-to-br from-blue-50 to-white dark:from-slate-500 dark:to-slate-700">
    <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-slate-300">
      {t('profit')} / {t('margin')}
    </div>
    <div className="mt-1 text-[20px] font-semibold text-blue-700 tabular-nums">
      ${profit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </div>
    <div className="text-[11px] text-gray-500 dark:text-slate-300">
      {margin.toFixed(1)}% {t('margin')}
    </div>
  </div>
</div>
                        )}


                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ARAPChargesContainer;
